import { Play, Pause, ShoppingCart, Sparkles } from "lucide-react";
import { dollars } from "@/lib/api";
import { useMeloMonday } from "@/context/MeloMondayContext";
import ExplicitBadge from "@/components/ExplicitBadge";

/**
 * Single row in a track list. Handles its own play/pause button state,
 * artist/genre columns, and the buy-or-free CTA on the right.
 * Automatically flips to a free-download CTA when this track is the
 * artist's Melo Monday pick and today is Monday.
 */
export default function TrackRow({ track, index, isCurrent, isPlaying, showBuy, onPlayToggle, onBuy }) {
  const mm = useMeloMonday();
  const isMondayFree = mm.isFree(track.id);
  return (
    <div
      data-testid={`track-row-${track.id}`}
      className="grid grid-cols-[40px_1fr_auto] md:grid-cols-[40px_1fr_1fr_auto_auto] items-center gap-3 md:gap-4 px-3 md:px-4 py-3 hover:bg-white/[0.03] transition-colors group"
    >
      <button
        data-testid={`play-track-${track.id}`}
        onClick={onPlayToggle}
        className="w-8 h-8 grid place-items-center text-gray-500 group-hover:text-[#FF4500] transition-colors"
      >
        {isCurrent && isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <>
            <span className="group-hover:hidden font-mono text-xs">{String(index + 1).padStart(2, "0")}</span>
            <Play className="h-4 w-4 hidden group-hover:block" fill="currentColor" />
          </>
        )}
      </button>

      <div className="min-w-0">
        <div className="font-display truncate flex items-center gap-2">
          <span className="truncate">{track.title}</span>
          <ExplicitBadge explicit={track.is_explicit} trackId={track.id} size="xs" />
          {isMondayFree && (
            <span
              data-testid={`melo-badge-${track.id}`}
              className="inline-flex items-center gap-1 shrink-0 bg-[#FF4500] text-black font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5"
            >
              <Sparkles className="h-2.5 w-2.5" /> Free Today
            </span>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 truncate md:hidden">
          {track.artist_name}
        </div>
      </div>
      <div className="hidden md:block font-mono text-xs text-gray-400 truncate">{track.artist_name}</div>
      <div className="hidden md:block font-mono text-xs text-gray-500 uppercase">{track.genre || "—"}</div>

      {showBuy && <TrackPriceCta track={track} isMondayFree={isMondayFree} onBuy={onBuy} />}
    </div>
  );
}

function TrackPriceCta({ track, isMondayFree, onBuy }) {
  // Melo Mondays: even paid tracks download for free today (backend allows it).
  if (track.is_free || isMondayFree) {
    return (
      <a
        data-testid={`dl-free-${track.id}`}
        href={`/api/tracks/${track.id}/download`}
        className="font-mono text-xs uppercase tracking-widest text-[#FF4500] hover:text-white flex items-center gap-1 whitespace-nowrap"
      >
        <ShoppingCart className="h-3.5 w-3.5" /> FREE
      </a>
    );
  }
  return (
    <button
      data-testid={`buy-track-${track.id}`}
      onClick={onBuy}
      className="font-mono text-xs uppercase tracking-widest text-[#FF4500] hover:text-white flex items-center gap-1 whitespace-nowrap"
    >
      <ShoppingCart className="h-3.5 w-3.5" />
      {dollars(track.price_cents || 99)}
    </button>
  );
}
